// src/sectors/pilates/sessions/modals/kinds/SessionCreatePackageModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { minToHm } from '../utils/time';
import { TrainerSection } from '../sections/TrainerSection';
import { CustomerSection } from '../sections/CustomerSection';
import { PackageConsumeSection } from '../sections/PackageConsumeSection';
import { PackageCreateModal } from 'sectors/pilates/members/modal/PackageCreateModal/PackageCreateModal';
import { MemberPackageCard } from '../../MemberPackageCard';
import { ConfirmDialog } from '../../parts/ConfirmDialog';

import { pickActivePackage, normalizeItems } from '../../packageUtils'; // ✅ doğru path senin projene göre olabilir
import { uiBus } from 'shared/api/uiBus';

function CapacityWarning({ tokens, text }) {
  const theme = useMuiTheme();
  const surface = tokens?.surface || '#fff';
  if (!text) return null;

  return (
    <MDBox
      sx={{
        borderRadius: '18px',
        border: `1px solid ${tokens?.border || theme.palette.divider}`,
        background: surface,
        p: 1,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <MDBox
        sx={{
          width: 34,
          height: 34,
          borderRadius: '14px',
          display: 'grid',
          placeItems: 'center',
          border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
          background: alpha(theme.palette.warning.main, 0.12),
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>warning</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.15 }}>
          Uyarı
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{ opacity: 0.85, display: 'block', mt: 0.2, lineHeight: 1.35 }}
        >
          {text}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

export function SessionCreatePackageModal({
  open,
  onClose,
  onBack,
  onValidate,
  onCreate,
  initialDraft, // ✅ NEW
  dayISO,
  startMin,
  defaultTrainerId = 'ALL',
  tokens,
  trainers = [],
  staffLoading = false,
  staffError = false,
  loadMemberPackages, // async(memberId)=>packages[]
  onCreated,
}) {
  const theme = useMuiTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // ✅ validate → decision → create standard
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const [saving, setSaving] = useState(false);
  const lastPayloadRef = useRef(null);
  // selections
  const [trainerId, setTrainerId] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  // consume options
  const [consumeType, setConsumeType] = useState('NORMAL'); // NORMAL | TELAFI
  const [note, setNote] = useState('');

  // packages by member
  const [pkgByMember, setPkgByMember] = useState({});
  const pkgByMemberRef = useRef({});
  useEffect(() => {
    pkgByMemberRef.current = pkgByMember;
  }, [pkgByMember]);

  const [chosenItemByMember, setChosenItemByMember] = useState({});
  const [activityKey, setActivityKey] = useState('');

  // create package modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createFor, setCreateFor] = useState(null);

  // confirm trainer mismatch
  const [confirmOpen, setConfirmOpen] = useState(false);

  // reset on open
  useEffect(() => {
    if (!open) return;

    // her açılışta temel temizlik
    setPkgByMember({});
    setChosenItemByMember({});
    setCreateOpen(false);
    setCreateFor(null);
    setConfirmOpen(false);
    setDecision(null);
    setDecisionOpen(false);
    setSaving(false);
    lastPayloadRef.current = null;
    // ✅ EDIT MODE
    if (initialDraft?.sessionId) {
      setTrainerId(String(initialDraft.trainerId || ''));

      const parts = Array.isArray(initialDraft.participants) ? initialDraft.participants : [];

      const midsNum = parts
        .map((p) => p?.memberId)
        .filter((x) => x != null)
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x));

      setMemberIds(midsNum);

      // UI seçili üyeler (CustomerSection buna bakıyorsa)
      setSelectedMembers(
        parts.map((p) => ({
          id: p?.memberId,
          fullName: p?.memberNameSnapshot || p?.memberName || p?.fullName || '',
          trainerId: p?.trainerId ?? null,
          trainerName: p?.trainerNameSnapshot || p?.trainerName || null,
        }))
      );
      // ✅ ASIL: editte seçili packageItemId’leri set et
      setChosenItemByMember(
        Object.fromEntries(
          parts
            .filter((p) => p?.memberId != null && p?.packageItemId != null)
            .map((p) => [String(p.memberId), String(p.packageItemId)])
        )
      );
      setConsumeType(initialDraft.sessionType || 'NORMAL');
      setNote(initialDraft.note || '');
      setActivityKey(String(initialDraft.activityName || '').trim());

      // ✅ Editte hangi paket kalemi seçilmişse onu seçili getir
      setChosenItemByMember(
        Object.fromEntries(
          parts
            .filter((p) => p?.memberId != null && p?.packageItemId != null)
            .map((p) => [String(p.memberId), String(p.packageItemId)])
        )
      );

      return;
    }

    // ✅ CREATE MODE
    setTrainerId('');
    setMemberIds([]);
    setSelectedMembers([]);
    setConsumeType('NORMAL');
    setNote('');
    setActivityKey('');
  }, [open, initialDraft]);

  // default trainer
  useEffect(() => {
    if (!open) return;
    const list = Array.isArray(trainers) ? trainers : [];
    const tId =
      defaultTrainerId !== 'ALL'
        ? String(defaultTrainerId)
        : list[0]?.id != null
        ? String(list[0].id)
        : '';
    setTrainerId((prev) => prev || tId);
  }, [open, defaultTrainerId, trainers]);

  // refresh single member package (used after create)
  const refreshMemberPackage = async (mid) => {
    const id = String(mid || '');
    if (!id) return;

    setPkgByMember((prev) => ({
      ...prev,
      [id]: { pkg: null, items: [], loading: true, error: false },
    }));

    try {
      const list = (await loadMemberPackages?.(id)) || [];
      const picked = pickActivePackage(list);
      const items = picked ? normalizeItems(picked) : [];

      setPkgByMember((prev) => ({
        ...prev,
        [id]: { pkg: picked || null, items, loading: false, error: !picked },
      }));

      const firstId = items?.[0]?.id || '';
      if (firstId) {
        setChosenItemByMember((prev) => ({ ...prev, [id]: prev[id] || firstId }));
      }
    } catch (e) {
      setPkgByMember((prev) => ({
        ...prev,
        [id]: { pkg: null, items: [], loading: false, error: true },
      }));
    }
  };

  // ✅ fetch packages when memberIds change (NO infinite loop)
  useEffect(() => {
    if (!open) return;
    const ids = (memberIds || []).map(String);

    // cleanup removed
    setPkgByMember((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!ids.includes(String(id))) delete next[id];
      });
      return next;
    });
    setChosenItemByMember((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!ids.includes(String(id))) delete next[id];
      });
      return next;
    });

    let cancelled = false;

    (async () => {
      const snapshot = pkgByMemberRef.current || {};

      const missing = ids.filter((id) => {
        const cur = snapshot[id];
        return !(cur?.loading || cur?.pkg || cur?.error);
      });

      if (missing.length === 0) return;

      // mark loading
      setPkgByMember((prev) => {
        const next = { ...prev };
        missing.forEach((id) => {
          next[id] = { pkg: null, items: [], loading: true, error: false };
        });
        return next;
      });

      await Promise.all(
        missing.map(async (id) => {
          try {
            const list = (await loadMemberPackages?.(id)) || [];
            const picked = pickActivePackage(list);
            const items = picked ? normalizeItems(picked) : [];

            if (cancelled) return;

            setPkgByMember((prev) => ({
              ...prev,
              [id]: { pkg: picked || null, items, loading: false, error: !picked },
            }));

            const firstId = items?.[0]?.id || '';
            if (firstId) {
              setChosenItemByMember((prev) => ({ ...prev, [id]: prev[id] || firstId }));
            }
          } catch (e) {
            if (cancelled) return;
            setPkgByMember((prev) => ({
              ...prev,
              [id]: { pkg: null, items: [], loading: false, error: true },
            }));
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [open, memberIds, loadMemberPackages]);

  // common activity options
  const activityOptions = useMemo(() => {
    const ids = (memberIds || []).map(String);
    if (ids.length === 0) return [];

    const lists = ids
      .map((id) => pkgByMember[id]?.items || [])
      .filter((arr) => Array.isArray(arr) && arr.length > 0);

    if (lists.length !== ids.length) return [];

    const sets = lists.map(
      (arr) => new Set(arr.map((x) => (x?.activityName || '').trim()).filter(Boolean))
    );

    let inter = null;
    for (const s of sets)
      inter = inter == null ? new Set(s) : new Set([...inter].filter((x) => s.has(x)));
    const common = inter ? Array.from(inter) : [];

    return common.map((name) => ({ key: name, label: name }));
  }, [memberIds, pkgByMember]);

  useEffect(() => {
    if (!open) return;
    if (!activityKey) return;

    const ids = (memberIds || []).map(String);
    ids.forEach((id) => {
      const items = pkgByMember[id]?.items || [];
      const found = items.find((x) => String(x.activityName) === String(activityKey));
      if (found?.id) setChosenItemByMember((prev) => ({ ...prev, [id]: String(found.id) }));
    });
  }, [open, activityKey, memberIds, pkgByMember]);

  const capacityWarningText = useMemo(() => {
    const ids = (memberIds || []).map(String);
    const caps = ids.map((id) => pkgByMember[id]?.pkg?.classCapacity).filter((x) => x != null);
    if (caps.length !== ids.length) return '';
    const uniq = Array.from(new Set(caps.map(String)));
    if (uniq.length <= 1) return '';
    return `Seçili üyelerin paket kapasitesi farklı görünüyor (${uniq.join(
      ' / '
    )}). Aynı seansa eklemek sorun çıkarabilir.`;
  }, [memberIds, pkgByMember]);

  const trainerMismatchText = useMemo(() => {
    if (!trainerId) return '';

    const mismatched = (selectedMembers || []).filter((m) => {
      const midTrainerId = m?.trainerId != null ? String(m.trainerId) : '';
      if (!midTrainerId) return false; // üyede trainer yoksa karışma
      return midTrainerId !== String(trainerId);
    });

    if (mismatched.length === 0) return '';

    const pickedTrainerName =
      (trainers || []).find((t) => String(t.id) === String(trainerId))?.name || `#${trainerId}`;

    if (mismatched.length === 1) {
      const m = mismatched[0];
      const memberTrainerLabel =
        m?.trainerName || (m?.trainerId != null ? `#${m.trainerId}` : 'Bilinmiyor');
      return `${m.fullName} üyesi normalde "${memberTrainerLabel}" Eğitmenya bağlı.\nBu seansı "${pickedTrainerName}" Eğitmensına yazmak istiyor musun?`;
    }

    const names = mismatched
      .slice(0, 3)
      .map((x) => x.fullName)
      .join(', ');
    const more = mismatched.length > 3 ? ` (+${mismatched.length - 3})` : '';

    return `Seçili üyelerin bazıları farklı Eğitmenlara bağlı görünüyor: ${names}${more}\nBu seansı "${pickedTrainerName}" Eğitmensına yazmak istiyor musun?`;
  }, [trainerId, selectedMembers, trainers]);

  const canSave = useMemo(() => {
    if (!open) return false;
    if (!dayISO || typeof startMin !== 'number') return false;
    if (!trainerId) return false;
    if ((memberIds?.length || 0) === 0) return false;

    for (const mid of memberIds) {
      const st = pkgByMember[String(mid)];
      if (!st || st.loading) return false;
      if (!st.pkg) return false;
      if ((st.items?.length || 0) === 0) return false;
      if (!chosenItemByMember[String(mid)]) return false;
    }

    if ((memberIds?.length || 0) > 1) {
      if (!activityKey) return false;
      if ((activityOptions?.length || 0) === 0) return false;
    }

    return true;
  }, [
    open,
    dayISO,
    startMin,
    trainerId,
    memberIds,
    pkgByMember,
    chosenItemByMember,
    activityKey,
    activityOptions,
  ]);

  const onCreatePackage = (mid) => {
    setCreateFor(String(mid));
    setCreateOpen(true);
  };

  const removeMember = (memberId) => {
    setMemberIds((prev) =>
      Array.isArray(prev) ? prev.filter((x) => String(x) !== String(memberId)) : []
    );
  };

  const buildPayload = () => {
    const t = (trainers || []).find((x) => String(x.id) === String(trainerId));
    const trainerName = String(t?.name || '').trim();
    if (!trainerName) throw new Error('Eğitmen adı bulunamadı. Eğitmeni tekrar seç.');

    const ids = (memberIds || []).map(String);

    const firstMid = ids[0] || '';
    const firstItems = pkgByMember[firstMid]?.items || [];
    const firstChosenId = chosenItemByMember[firstMid] || firstItems?.[0]?.id || null;
    const firstChosen = firstItems.find((x) => String(x.id) === String(firstChosenId)) || null;

    const finalActivityName =
      ids.length > 1
        ? String(activityKey || '').trim()
        : String(firstChosen?.activityName || activityKey || '').trim();

    if (!finalActivityName) throw new Error('Aktivite adı boş olamaz. Lütfen aktivite seç.');

    const caps = ids
      .map((mid) => pkgByMember[mid]?.pkg?.classCapacity)
      .filter((x) => typeof x === 'number' && x > 0);
    const capacity = caps.length ? Math.min(...caps) : 1;

    const participants = ids.map((mid) => {
      const st = pkgByMember[mid];
      const pkg = st?.pkg;
      const items = st?.items || [];
      const chosenId = chosenItemByMember[mid] || items?.[0]?.id || null;

      return {
        memberId: Number(mid),
        memberPackageId: pkg?.id != null ? Number(pkg.id) : null,
        packageItemId: chosenId != null ? Number(chosenId) : null,
        note: null,
      };
    });

    return {
      sessionId: initialDraft?.sessionId || null,
      dayISO,
      startMin: Number(startMin),
      durationMin: 60,

      trainerId: Number(trainerId),
      trainerName,

      activityId: null,
      activityName: finalActivityName,

      capacity,
      billingMode: 'PACKAGE',
      sessionType: consumeType,
      autoApprove: true,
      note: note?.trim() || null,
      participants,
    };
  };
  const doValidate = async (payload) => {
    const res = await onValidate?.(payload);
    return res?.data ?? res;
  };

  const doCreate = async (payload) => {
    const res = await onCreate?.(payload);
    return res?.data ?? res;
  };
  const handleSave = async () => {
    if (!canSave || saving) return;

    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      uiBus.emit('toast', {
        type: 'error',
        message: e?.message || 'Form hatalı.',
      });
      return;
    }

    lastPayloadRef.current = payload;
    setSaving(true);

    try {
      const dec = await doValidate(payload);
      setDecision(dec);

      const blocks = Array.isArray(dec?.blocks) ? dec.blocks : [];
      const warnings = Array.isArray(dec?.warnings) ? dec.warnings : [];

      // block veya warning varsa dialog aç
      if (blocks.length || warnings.length) {
        setDecisionOpen(true);
        return;
      }

      // temizse direkt create
      // temizse direkt create
      const res = await doCreate(payload);
      await onCreated?.(res, payload);

      uiBus.emit('toast', {
        type: 'success',
        message: 'Seans başarıyla kaydedildi.',
      });

      onClose?.();
    } catch (err) {
      console.error('[SessionCreatePackageModal] save failed', err);

      uiBus.emit('toast', {
        type: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Seans kaydedilemedi.',
      });
    } finally {
      setSaving(false);
    }
  };
  const handleSaveClick = () => {
    if (!canSave || saving) return;

    if (trainerMismatchText) {
      setConfirmOpen(true);
      return;
    }

    handleSave(); // ✅ confirm yoksa direkt
  };

  const handleProceedDespiteWarnings = async () => {
    if (saving) return;
    const payload = lastPayloadRef.current;
    if (!payload) return;

    setSaving(true);
    try {
      const res = await doCreate(payload);
      setDecisionOpen(false);
      await onCreated?.(res, payload);

      uiBus.emit('toast', {
        type: 'success',
        message: 'Seans başarıyla kaydedildi.',
      });

      onClose?.();
    } catch (err) {
      console.error('[SessionCreatePackageModal] create despite warnings failed', err);

      uiBus.emit('toast', {
        type: 'error',
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Seans kaydedilemedi.',
      });
    } finally {
      setSaving(false);
    }
  };

  const title = dayISO ? `${dayISO} • ${minToHm(startMin)}` : 'Paket Seansı';
  const trainerMismatchBanner = useMemo(() => {
    if (!trainerMismatchText) return '';
    return 'Seçili üyelerin Eğitmenları seçilen Eğitmenyla uyuşmuyor. Kaydederken onay isteyeceğim.';
  }, [trainerMismatchText]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '22px',
          overflow: 'hidden',
          background: tokens?.isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
          border: `1px solid ${tokens?.border || theme.palette.divider}`,
          boxShadow: fullScreen
            ? 'none'
            : '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
          backgroundImage: 'none',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: fullScreen ? 'rgba(0,0,0,.55)' : 'rgba(0,0,0,.45)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.1 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
            <MDBox
              sx={{
                width: 42,
                height: 42,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                background: alpha(theme.palette.info.main, 0.12),
                border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>redeem</Icon>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                Paket Seansı
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.78, display: 'block', mt: 0.35 }}>
                {title}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDBox display="flex" gap={0.6}>
            <MDButton
              variant="text"
              color="secondary"
              onClick={onBack}
              sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
            >
              <Icon>arrow_back</Icon>
            </MDButton>
            <MDButton
              variant="text"
              color="secondary"
              onClick={onClose}
              sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
            >
              <Icon>close</Icon>
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 1.6, pb: 1.3 }}>
        <MDBox sx={{ display: 'grid', gap: 1.1 }}>
          <TrainerSection
            sx={tokens?.card}
            tokens={tokens}
            trainerId={trainerId}
            setTrainerId={setTrainerId}
            trainers={trainers}
            loading={staffLoading}
            error={staffError}
          />

          <CustomerSection
            sx={tokens?.card}
            tokens={tokens}
            trainerId={trainerId}
            selectedIds={memberIds}
            setSelectedIds={setMemberIds}
            onSelectedMembersChange={setSelectedMembers}
          />

          <CapacityWarning tokens={tokens} text={capacityWarningText} />
          <CapacityWarning tokens={tokens} text={trainerMismatchBanner} />

          {(memberIds || []).map((mid) => (
            <MemberPackageCard
              key={String(mid)}
              tokens={tokens}
              memberId={String(mid)}
              pkgState={
                pkgByMember[String(mid)] || { pkg: null, items: [], loading: false, error: false }
              }
              consumeType={consumeType}
              chosenItemId={chosenItemByMember[String(mid)] || ''}
              onRemove={removeMember}
              onCreatePackage={onCreatePackage}
            />
          ))}

          <PackageCreateModal
            open={createOpen}
            memberId={createFor}
            onClose={() => setCreateOpen(false)}
            onCreated={async () => {
              const id = createFor;
              setCreateOpen(false);
              setCreateFor(null);
              await refreshMemberPackage(id);
            }}
          />

          <PackageConsumeSection
            sx={tokens?.card}
            tokens={tokens}
            consumeType={consumeType}
            setConsumeType={setConsumeType}
            note={note}
            setNote={setNote}
            activityOptions={activityOptions}
            activityKey={activityKey}
            setActivityKey={setActivityKey}
            activityHint={
              (memberIds?.length || 0) > 1
                ? 'Çoklu seans için üyelerin ortak aktivitesini seç.'
                : 'Tek üye seçiliyse aktivite otomatik paket kartından gelir.'
            }
          />
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 2,
          pt: 1.4,
          gap: 1,
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: fullScreen ? 'column' : 'row',
        }}
      >
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          fullWidth={fullScreen}
          sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
        >
          Vazgeç
        </MDButton>

        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSaveClick}
          disabled={!canSave || saving}
          fullWidth={fullScreen}
          sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
        >
          {saving ? 'Kontrol ediliyor...' : 'Kaydet'}
        </MDButton>

        <ConfirmDialog
          open={confirmOpen}
          title="Eğitmen Uyumsuzluğu"
          message={trainerMismatchText}
          confirmText="Evet, kaydet"
          cancelText="Vazgeç"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            handleSave();
          }}
        />
        <ConfirmDialog
          open={decisionOpen}
          title={decision?.blocks?.length || 0 ? 'Kaydedilemedi' : 'Uyarı'}
          subtitle={
            decision?.blocks?.length || 0
              ? 'Bu seans şu an kaydedilemez.'
              : 'Uyarılar var. Yine de kaydetmek istiyor musun?'
          }
          tone={decision?.blocks?.length || 0 ? 'error' : 'warning'}
          tokens={tokens}
          items={
            decision?.blocks?.length || 0
              ? (decision.blocks || []).map((b) => ({
                  title: b?.code || 'BLOCK',
                  message: b?.message || '',
                }))
              : (decision?.warnings || []).map((w) => ({
                  title: w?.code || 'WARNING',
                  message: w?.message || w || '',
                }))
          }
          confirmText={decision?.blocks?.length || 0 ? 'Tamam' : 'Evet, kaydet'}
          cancelText="Vazgeç"
          hideCancel={(decision?.blocks?.length || 0) > 0} // block varsa cancel gizle, sadece "Tamam"
          busy={saving}
          onCancel={() => {
            setDecisionOpen(false);
            setDecision(null);
          }}
          onConfirm={() => {
            // block ise sadece kapat
            if ((decision?.blocks?.length || 0) > 0) {
              setDecisionOpen(false);
              setDecision(null);
              return;
            }
            // warning ise devam et
            handleProceedDespiteWarnings();
          }}
        />
      </DialogActions>
    </Dialog>
  );
}
